import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Route, Switch, Redirect, Link
} from 'react-router-dom';
import classNames from 'classnames';
import { withRouter, matchPath } from 'react-router';
import urlJoin from 'url-join';
import fp from 'lodash/fp';
import defaultStyles from './Tab.module.scss';

const lastTabs = {};

const createHeader = (item, innerProps) => {
    const active = matchPath(
        innerProps.location.pathname,
        urlJoin(innerProps.match.url, item.path)
    );
    const className = classNames(innerProps.styles.tabHandle, {
        [innerProps.styles.underlineInactive]: innerProps.alwaysUnderline,
        [innerProps.styles.thickUnderline]: innerProps.thickUnderline,
        [innerProps.styles.active]: active
    });
    return (
        <Link
            to={ urlJoin(innerProps.match.url, item.path) }
            style={ {
                borderBottomWidth: `${innerProps.underlineThickness}px`
            } }
            className={ className }
            key={ item.path }
        >
            <div
                className={ innerProps.styles.tabHandleLabel }
                data-text={ item.title }
            >{ item.title }
            </div>
        </Link>
    );
};

const createRoute = (tab, innerProps) => (
    <Route
        key={ tab.path }
        id={ tab.path }
        path={ urlJoin(innerProps.match.url, tab.path) }
        render={ tab.component }
    />
);

const createRedirect = (innerProps) => {
    if (innerProps.tabs && innerProps.tabs.length) {
        return (
            <Route
                render={ () => {
                    const redirectRoute = innerProps.rememberLastTab && lastTabs[innerProps.match.url.toLowerCase()]
                        ? urlJoin(innerProps.match.url, lastTabs[innerProps.match.url.toLowerCase()])
                        : urlJoin(innerProps.match.url, innerProps.tabs[0].path);
                    return (
                        <Redirect to={ redirectRoute } />
                    );
                } }
            />
        );
    }
    return <Route render={ () => ('No tabs to display') } />;
};

const Tab = ({ Version, HeaderComponent, ...props }) => {
    useEffect(() => {
        const root = props.match.url.toLowerCase();
        const match = fp.find((tab) => props.location.pathname.toLowerCase()
            === urlJoin(props.match.url, tab.path).toLowerCase())(props.tabs);
        if (match && props.rememberLastTab) {
            lastTabs[root] = match.path;
        }
    }, [props.location.pathname, props.match.url, props.tabs, props.rememberLastTab]);

    return (

        <div className={ classNames([props.styles.tab, { [props.styles.compact]: props.compact }]) }>
            <div className={ props.styles.headerContainer }>
                <header className={ props.styles.header }>
                    { props.tabs.map((item) => createHeader(item, props)) }
                </header>
                { HeaderComponent && <HeaderComponent /> }
                { props.showVersion ? (
                    <div className={ props.styles.versionContainer }>
                        { Version && <Version /> }
                    </div>
                ) : null }
            </div>
            <div className={ props.styles.tabContent }>
                <Switch>
                    { props.tabs.map((item) => createRoute(item, props)) }
                    { createRedirect(props) }
                </Switch>
            </div>
        </div>
    );
};

Tab.propTypes = {
    HeaderComponent: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.func
    ]),
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            path: PropTypes.string,
            component: PropTypes.elementType
        })
    ),
    match: PropTypes.shape({
        url: PropTypes.string
    }).isRequired,
    compact: PropTypes.bool,
    showVersion: PropTypes.bool,
    Version: PropTypes.func,
    styles: PropTypes.objectOf(PropTypes.string),
    alwaysUnderline: PropTypes.bool,
    underlineThickness: PropTypes.number,
    withDivider: PropTypes.bool,
    location: PropTypes.shape({
        pathname: PropTypes.string
    }).isRequired,
    rememberLastTab: PropTypes.bool
};

Tab.defaultProps = {
    HeaderComponent: null,
    tabs: [],
    compact: false,
    showVersion: false,
    Version: null,
    styles: defaultStyles,
    alwaysUnderline: true,
    underlineThickness: 2,
    withDivider: false,
    rememberLastTab: false
};

export default withRouter(Tab);
